import useJobStream from '../hooks/useJobStream';
import LiveJobStatus from '../components/LiveJobStatus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Nav, Spinner, Tab, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import { operationLabel } from '../features/aiRigging';
import { shortHash, shortJobId } from '../features/riggingJobStatus';
import RiggingJobStatusBadge from '../components/rigging/RiggingJobStatusBadge';
import RiggingPipelineProgress from '../components/rigging/RiggingPipelineProgress';
import RiggingRuntimeNotice from '../components/rigging/RiggingRuntimeNotice';
import RiggingRuntimeRequirements from '../components/rigging/RiggingRuntimeRequirements';
import RiggingExecutionPackageSummary from '../components/rigging/RiggingExecutionPackageSummary';
import ProjectBreadcrumbs from '../components/ProjectBreadcrumbs';
import { hasTenantPermission, JOB_CANCEL, RIGGING_JOB_VIEW } from '../auth/permissions';
import { useTenantPermissions } from '../hooks/useTenantPermissions';

export default function ProjectRiggingJobDetailPage() {
  const { projectId, jobId } = useParams();
  const { permissions, hasTenantPermission: can } = useTenantPermissions();
  const [job, setJob] = useState(null);
  const [artifacts, setArtifacts] = useState([]);
  const [projectName, setProjectName] = useState('Project');
  const [loading, setLoading] = useState(true);
  const [artifactError, setArtifactError] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [cancelling, setCancelling] = useState(false);

  const canView =
    can(RIGGING_JOB_VIEW) || hasTenantPermission(permissions, RIGGING_JOB_VIEW);
  const canCancel = can(JOB_CANCEL) || hasTenantPermission(permissions, 'job.cancel');

  const { revision: liveRevision, status: liveStatus } = useJobStream({ jobId, projectId, rigging: true, enabled: canView });

  const loadGeneration = useRef(0);
  const load = useCallback(async (background = false) => {
    const generation = ++loadGeneration.current;
    if (!background) setLoading(true);
    setError(null);
    try {
      const [detail, projectData] = await Promise.all([
        bakingApi.getRiggingJob(projectId, jobId),
        bakingApi.getProject(projectId).catch(() => null),
      ]);
      if (generation !== loadGeneration.current) return;
      setJob(detail);
      if (projectData?.name) setProjectName(projectData.name);
      try {
        const arts = await bakingApi.listJobArtifacts(jobId);
        if (generation !== loadGeneration.current) return;
        setArtifacts(arts || []);
        setArtifactError(null);
      } catch (err) {
        if (generation !== loadGeneration.current) return;
        setArtifacts([]);
        setArtifactError(err.message || 'Failed to load artifacts');
      }
    } catch (err) {
      if (generation === loadGeneration.current) setError(err.message || 'Failed to load rigging job');
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [projectId, jobId]);

  useEffect(() => {
    load(liveRevision > 0);
    return () => { loadGeneration.current += 1; };
  }, [load, liveRevision]);

  const cancelJob = async () => {
    if (
      !window.confirm(
        'Cancel this rigging job? A running worker will be asked to stop, and any partial outputs may be discarded or retained according to platform policy.'
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      await bakingApi.cancelJob(jobId);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to cancel job');
    } finally {
      setCancelling(false);
    }
  };

  if (!canView && permissions.length) {
    return <div className="error-state">You do not have permission to view this job.</div>;
  }
  if (loading) {
    return (
      <div className="py-4">
        <Spinner animation="border" size="sm" /> Loading job…
      </div>
    );
  }
  if (error && !job) return <div className="error-state">{error}</div>;
  if (!job) return <div className="empty-state">Job not found.</div>;

  const req = job.pipeline_request || {};
  const runtimeEnabled = Boolean(job.runtime_availability?.worker_runtime_enabled);
  const latestAttempt = (job.attempts || [])[(job.attempts || []).length - 1];

  return (
    <div data-testid="rigging-job-detail">
      <LiveJobStatus status={liveStatus} />
      <ProjectBreadcrumbs
        items={[
          { label: 'Projects', to: '/console/projects' },
          { label: projectName, to: `/console/projects/${projectId}` },
          { label: 'Rigging Jobs', to: `/console/projects/${projectId}/rigging/jobs` },
          { label: shortJobId(job.id) },
        ]}
      />
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h1 className="product-page-title mb-1">Rigging Job {shortJobId(job.id)}</h1>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <RiggingJobStatusBadge job={job} workerRuntimeEnabled={runtimeEnabled} />
            <span>{operationLabel(job.operation)}</span>
            <span className="text-muted">{req.character_display_name}</span>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button
            as={Link}
            variant="outline-secondary"
            size="sm"
            to={`/console/projects/${projectId}/rigging/jobs/new?character=${req.character || ''}`}
          >
            Create New Job From This
          </Button>
          {canCancel && !['succeeded', 'failed', 'canceled'].includes(job.status) ? (
            <Button
              variant="outline-danger"
              size="sm"
              disabled={cancelling}
              onClick={cancelJob}
            >
              {cancelling ? 'Cancelling…' : 'Cancel Job'}
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <div className="error-state mb-3">{error}</div> : null}
      <RiggingRuntimeNotice availability={job.runtime_availability} className="mb-3" />

      <Tab.Container activeKey={tab} onSelect={(k) => setTab(k || 'overview')}>
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="inputs">Inputs</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="attempts">Attempts</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="progress">Progress</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="artifacts">Artifacts</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="results">Results</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="proposals">Memory Proposals</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <div className="row g-3">
              <div className="col-lg-6">
                <div className="product-card h-100">
                  <h3 className="h6">Summary</h3>
                  <dl className="row mb-0">
                    <dt className="col-sm-4">Character</dt>
                    <dd className="col-sm-8">
                      <Link
                        to={`/console/projects/${projectId}/rigging/characters/${req.character}`}
                      >
                        {req.character_display_name}
                      </Link>
                    </dd>
                    <dt className="col-sm-4">Operation</dt>
                    <dd className="col-sm-8">{operationLabel(job.operation)}</dd>
                    <dt className="col-sm-4">Source scene</dt>
                    <dd className="col-sm-8 mono">{req.source_scene_path}</dd>
                    <dt className="col-sm-4">Rig Memory</dt>
                    <dd className="col-sm-8">
                      v{req.memory_version_number} · {shortHash(req.memory_configuration_sha256)}
                    </dd>
                    <dt className="col-sm-4">Created</dt>
                    <dd className="col-sm-8">
                      {job.created_at ? new Date(job.created_at).toLocaleString() : '—'}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="product-card h-100">
                  <h3 className="h6">Pipeline</h3>
                  <RiggingPipelineProgress
                    operation={job.operation}
                    jobStatus={job.status}
                    attemptStatus={latestAttempt?.status}
                    hasArtifacts={artifacts.length > 0}
                    hasResult={(job.results || []).length > 0}
                    workerRuntimeEnabled={runtimeEnabled}
                  />
                </div>
              </div>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="inputs">
            <div className="product-card">
              <Alert variant="secondary" className="py-2">
                These inputs are immutable for this job.
              </Alert>
              <dl className="row">
                <dt className="col-sm-3">Character</dt>
                <dd className="col-sm-9">
                  {req.character_display_name} ({req.character_slug})
                </dd>
                <dt className="col-sm-3">Character Rig Memory</dt>
                <dd className="col-sm-9 mono">
                  {req.rig_memory_version} · v{req.memory_version_number} ·{' '}
                  {req.memory_configuration_sha256}
                </dd>
                <dt className="col-sm-3">Project policy</dt>
                <dd className="col-sm-9 mono">
                  {req.project_policy_version} · v{req.policy_version_number} ·{' '}
                  {req.policy_configuration_sha256}
                </dd>
                <dt className="col-sm-3">Source revision</dt>
                <dd className="col-sm-9">
                  {req.source_type === 'perforce'
                    ? `Changelist ${req.source_resolved_changelist}`
                    : `Uploaded · ${shortHash(req.source_content_sha256)}`}
                </dd>
                <dt className="col-sm-3">Source scene</dt>
                <dd className="col-sm-9 mono">{req.source_scene_path}</dd>
                <dt className="col-sm-3">Execution options</dt>
                <dd className="col-sm-9 mono">
                  <pre className="mb-0 small">
                    {JSON.stringify(req.execution_options || {}, null, 2)}
                  </pre>
                </dd>
              </dl>
              <h4 className="h6">Runtime Requirements</h4>
              <RiggingRuntimeRequirements requirements={job.runtime_requirements} />
              <h4 className="h6 mt-3">Execution Package</h4>
              <RiggingExecutionPackageSummary packageRecord={job.execution_package} />
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="attempts">
            <div className="product-card">
              {(job.attempts || []).length === 0 ? (
                <p className="mb-0 text-muted">No worker has claimed this job.</p>
              ) : (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Worker</th>
                      <th>Lease</th>
                      <th>Started</th>
                      <th>Finished</th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.attempts.map((a) => (
                      <tr key={a.id}>
                        <td>{a.attempt_number}</td>
                        <td>{a.status}</td>
                        <td>{a.assigned_worker_name || '—'}</td>
                        <td>{a.lease_active ? 'Active' : '—'}</td>
                        <td>{a.started_at ? new Date(a.started_at).toLocaleString() : '—'}</td>
                        <td>{a.finished_at ? new Date(a.finished_at).toLocaleString() : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {!runtimeEnabled ? (
                <Alert variant="warning" className="mb-0 mt-3">
                  This environment is not currently permitting AI Rigging workers to claim jobs.
                </Alert>
              ) : null}
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="progress">
            <div className="product-card" data-testid="rigging-progress-empty">
              <p className="mb-0 text-muted">
                Progress will appear here after the Maya/FabricatorStudio worker runtime is enabled
                and a worker claims the job.
              </p>
              <p className="small text-muted mt-2 mb-0">
                No simulated percentages or fake stage progress are shown.
              </p>
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="artifacts">
            <div className="product-card">
              {artifactError ? <div className="error-state mb-2">{artifactError}</div> : null}
              {artifacts.length === 0 ? (
                <p className="mb-0 text-muted">No artifacts have been uploaded for this job.</p>
              ) : (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Filename</th>
                      <th>SHA-256</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts.map((a) => (
                      <tr key={a.id}>
                        <td>{a.artifact_type}</td>
                        <td>{a.filename || a.original_filename || '—'}</td>
                        <td className="mono small">{shortHash(a.content_sha256)}</td>
                        <td>
                          {a.created_at ? new Date(a.created_at).toLocaleString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="results">
            <div className="product-card">
              {(job.results || []).length === 0 ? (
                <p className="mb-0 text-muted">
                  No structured result has been submitted for this job.
                </p>
              ) : (
                job.results.map((result) => (
                  <div key={result.id} className="mb-3">
                    <Alert variant="info" className="py-2">
                      Worker-reported result. Proposed changes require review before they can become
                      part of the Rigging Bible.
                    </Alert>
                    <dl className="row mb-0">
                      <dt className="col-sm-3">Status</dt>
                      <dd className="col-sm-9">{result.status}</dd>
                      <dt className="col-sm-3">Summary</dt>
                      <dd className="col-sm-9">{result.summary || '—'}</dd>
                    </dl>
                  </div>
                ))
              )}
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="proposals">
            <div className="product-card">
              {(job.proposals || []).length === 0 ? (
                <p className="mb-0 text-muted">No memory proposals are associated with this job.</p>
              ) : (
                <Table size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Rationale</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {job.proposals.map((p) => (
                      <tr key={p.id}>
                        <td>{p.proposal_type}</td>
                        <td>{p.lifecycle_status}</td>
                        <td>{p.rationale || '—'}</td>
                        <td>
                          {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                        </td>
                        <td>
                          <Link to={`/console/projects/${projectId}/rigging/proposals`}>
                            Review
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              <p className="small text-muted mt-2 mb-0">
                Accepting a proposal creates or updates a draft only. There is no Accept and Publish
                action.
              </p>
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}
